import { observer } from "mobx-react-lite";
import React, { useCallback, useLayoutEffect, useMemo } from "react";
import styles from "./edit-layout-page.module.scss";
import { useTranslation } from "react-i18next";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { Separator } from "@whatslly/ui-components/Separator";
import { Text } from "@whatslly/ui-components/Text";
import type { LayoutFieldsSetupType } from "@whatslly/types/LayoutFieldsSetupType";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import cloneDeep from "lodash/cloneDeep";
import isNumber from "lodash/isNumber";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";
import upperFirst from "lodash/upperFirst";
import { Page } from "@whatslly/ui-components/v2/Page";
import type { LayoutFieldType } from "@whatslly/types/LayoutFieldType";
import classnames from "classnames";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import type { GetLayoutFieldType } from "@whatslly/types/GetLayoutOptionsResponse";
import { LayoutStore } from "./store/LayoutStore";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { modalService } from "@whatslly/ui-components/modals";
import { AddNewLayoutFieldsModal } from "@whatslly/ui-components/modals/AddNewLayoutFieldsModal";
import type { SelectOptionSingleType } from "./editLayoutUtils";
import { addEmptyArrayToNotExistsFieldsType, fieldsToJs, getDisplayOptions, getFormLayoutsOptions, OPTION_VALUES } from "./editLayoutUtils";
import { FieldRow } from "./components/FieldRow";
import { ManageRecordTypesSection } from "./components/ManageRecordTypesSection";
import { DraggableListComponent } from "@whatslly/ui-components/v2/DraggableList/DraggableList";
import { DATE_AND_TIME_FIELDS_API_NAMES } from "@whatslly/commons/constants";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { ErrorBox } from "@whatslly/ui-components/ErrorBox";
import { AddEditNewRawFieldModal } from "@whatslly/ui-components/modals/AddEditNewRawFieldModal";
import { DEFAULT_MAX_LAYOUT } from "@whatslly/commons/OrgConfigsFeatures";
import { LayoutFieldsFilter } from "@whatsllyorg/crm-components/TuvisSettings/types/ILayoutFieldsProvider";

export type EditLayoutPageInitializeValue = "lead" | "contact" | "account" | "personAccount" | undefined;

export type EditLayoutsProps = {
  initializeValue: EditLayoutPageInitializeValue;
  forceReloadNotifications?: (data: { showWhatsllyTaskIcon: boolean | undefined; loginProvider: LoginProviderType | undefined }) => void;
  closePage?: (refreshPerson: boolean) => void;
  pageClassName?: string;
  mainWrapperClassName?: string;
  hideCloseAndRefreshButton?: boolean;
  loaderClassName?: string;
  footerClassName?: string;
  hideAddFields?: boolean;
  showAddRawField?: boolean;
  showEditButton?: boolean;
  readOnly?: boolean;
  filter?: LayoutFieldsFilter;
};

export const EditLayoutsPage: React.FC<EditLayoutsProps> = observer(
  ({
    initializeValue,
    closePage,
    forceReloadNotifications,
    pageClassName,
    hideCloseAndRefreshButton,
    mainWrapperClassName,
    loaderClassName,
    footerClassName,
    hideAddFields,
    showAddRawField,
    showEditButton,
    readOnly,
    filter,
  }) => {
    const layoutStore = useLocalStoreWithInjectedDependencies(LayoutStore);
    const { t } = useTranslation();

    useLayoutEffect(() => {
      if (layoutStore.fieldsAndSettings?.state === "fulfilled" && !layoutStore.initiated) {
        layoutStore.init(initializeValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [layoutStore.fieldsAndSettings?.state, initializeValue]);

    useLayoutEffect(() => {
      layoutStore.setFilters(filter);
    }, [filter?.getDefaultLayoutFields, filter?.orgId, filter?.profileName]);

    return (
      <>
        {layoutStore.fieldsAndSettings.case({
          pending: () => <Page isLoading loaderClassName={loaderClassName}></Page>,
          fulfilled: () => (
            <EditLayoutsPageBody
              initializeValue={initializeValue}
              closePage={closePage}
              forceReloadNotifications={forceReloadNotifications}
              pageClassName={pageClassName}
              mainWrapperClassName={mainWrapperClassName}
              hideCloseAndRefreshButton={hideCloseAndRefreshButton || readOnly}
              loaderClassName={loaderClassName}
              footerClassName={footerClassName}
              layoutStore={layoutStore}
              hideAddFields={hideAddFields}
              showAddRawField={showAddRawField}
              showEditButton={showEditButton}
              readOnly={readOnly}
            />
          ),
          rejected: () => (
            <Page hideFooter>
              <ErrorBox
                error={layoutStore.fieldsAndSettings.value ? String(layoutStore.fieldsAndSettings.value) : upperFirst(t("error"))}
                testId={`EditLayoutPageError`}
              />
            </Page>
          ),
        })}
      </>
    );
  }
);

export const EditLayoutsPageBody: React.FC<EditLayoutsProps & { layoutStore: LayoutStore }> = observer(
  ({
    closePage,
    forceReloadNotifications,
    pageClassName,
    hideCloseAndRefreshButton,
    mainWrapperClassName,
    loaderClassName,
    footerClassName,
    layoutStore,
    hideAddFields,
    showAddRawField,
    showEditButton,
    readOnly,
  }) => {
    const { t } = useTranslation();
    const settings = layoutStore.settings;
    const displayOptions = useMemo((): { label: string; value: keyof LayoutFieldsSetupType }[] => getDisplayOptions(t, settings), [t, settings]);

    const handleClosePage = useCallback(
      (refreshPerson: boolean) => {
        closePage?.(refreshPerson);
      },
      [closePage]
    );

    const formLayoutOptions = useMemo(() => getFormLayoutsOptions(t, settings), [settings, t]);

    const layoutOptions = useMemo(() => {
      const mergedOptions: SelectOptionSingleType[] = [...displayOptions, ...formLayoutOptions];

      mergedOptions.sort((a, b) => {
        const strippedA = a.value.startsWith("new") ? a.value.slice(3) : a.value;
        const strippedB = b.value.startsWith("new") ? b.value.slice(3) : b.value;

        if (strippedA === strippedB) {
          return a.value.startsWith("new") ? 1 : -1;
        }

        return strippedA.localeCompare(strippedB);
      });

      return mergedOptions;
    }, [displayOptions, formLayoutOptions]);

    const createCustomObjectFieldValues = useMemo(() => {
      return settings?.createCustomObject ? Object.keys(settings?.createCustomObject).map((key) => `new${upperFirst(key)}`) : [];
    }, [settings]);

    const showCustomObjectFieldValues = useMemo(() => {
      return settings?.showCustomObject ? Object.keys(settings?.showCustomObject).map((key) => key) : [];
    }, [settings]);

    const showRequiredCheckbox = useMemo(() => {
      const selectedValue = layoutStore.selectedLayout ? layoutStore.selectedLayout.value : "";
      return [
        ...createCustomObjectFieldValues,
        OPTION_VALUES.NEW_CASE,
        OPTION_VALUES.NEW_TASK,
        OPTION_VALUES.NEW_ACTIVITY,
        OPTION_VALUES.NEW_LEAD,
        OPTION_VALUES.NEW_CONTACT,
        OPTION_VALUES.NEW_ACCOUNT,
        OPTION_VALUES.NEW_EVENT,
        OPTION_VALUES.NEW_PERSON_ACCOUNT,
      ].includes(selectedValue);
    }, [createCustomObjectFieldValues, layoutStore.selectedLayout]);

    const isCreateCustomObjectField = useCallback(() => {
      if (!layoutStore.selectedLayout) return false;
      return createCustomObjectFieldValues.includes(layoutStore.selectedLayout.value);
    }, [createCustomObjectFieldValues, layoutStore.selectedLayout]);

    const isShowCustomObjectField = useCallback(() => {
      if (!layoutStore.selectedLayout) return false;
      return showCustomObjectFieldValues.includes(layoutStore.selectedLayout.value);
    }, [layoutStore.selectedLayout, showCustomObjectFieldValues]);

    const maxFields = useMemo(() => {
      const layoutSettingsLimit = layoutStore.selectedLayout ? settings?.layoutSettingsLimits?.[layoutStore.selectedLayout.value] : undefined;
      return isNumber(layoutSettingsLimit) ? layoutSettingsLimit : DEFAULT_MAX_LAYOUT;
    }, [layoutStore.selectedLayout, settings?.layoutSettingsLimits]);

    const isAddRawFieldEnabled = useMemo(() => {
      const selectedLayoutFieldsSetupToUse = layoutStore.currentFieldsSetup;
      return selectedLayoutFieldsSetupToUse && selectedLayoutFieldsSetupToUse.length < maxFields;
    }, [layoutStore.currentFieldsSetup, maxFields]);

    const isFieldDropdownEnabled = useMemo(() => {
      return isAddRawFieldEnabled && layoutStore.selectedLayoutAllFields.length > 0;
    }, [isAddRawFieldEnabled, layoutStore.selectedLayoutAllFields.length]);

    const getCreateCustomObjectKey = useCallback(() => {
      const objectKey = settings?.createCustomObject
        ? Object.keys(settings?.createCustomObject).find((key) => `new${upperFirst(key)}` === layoutStore.selectedLayout?.value)
        : undefined;
      if (!objectKey || !settings?.createCustomObject || !settings?.createCustomObject[objectKey]) return;
      return objectKey;
    }, [layoutStore.selectedLayout?.value, settings?.createCustomObject]);

    const getShowCustomObjectKey = useCallback(() => {
      const objectKey = settings?.showCustomObject
        ? Object.keys(settings?.showCustomObject).find((key) => key === layoutStore.selectedLayout?.value)
        : undefined;
      if (!objectKey || !settings?.showCustomObject || !settings?.showCustomObject[objectKey]) return;
      return objectKey;
    }, [layoutStore.selectedLayout?.value, settings?.showCustomObject]);

    const handleCustomObjects = useCallback(async () => {
      if (isCreateCustomObjectField() || isShowCustomObjectField()) {
        const objectKey = (() =>
          isCreateCustomObjectField() ? getCreateCustomObjectKey() : isShowCustomObjectField() ? getShowCustomObjectKey() : undefined)();
        if (!objectKey) return false;

        const customFields = await layoutStore.getCustomObjectFields(objectKey, layoutStore.selectedLayout.value, isCreateCustomObjectField());
        if (!customFields) return false;
        if (layoutStore.hasRecordTypes) {
          const defaultRecordType = Object.entries(layoutStore.fieldsClone).find(
            ([layoutKey]) => layoutKey.startsWith(layoutStore.selectedLayout.value) && layoutKey.endsWith(":default")
          );
          if (defaultRecordType) {
            layoutStore.setSelectedLayoutAllFields(defaultRecordType[1] as any[]);
            return;
          }
        }
        layoutStore.setSelectedLayoutAllFields(customFields.fields);
        return true;
      }
    }, [getCreateCustomObjectKey, getShowCustomObjectKey, isCreateCustomObjectField, isShowCustomObjectField, layoutStore]);
    const onSubmit = useCallback(async () => {
      if (!layoutStore.selectedLayout) return;
      const newStateFields = {
        ...addEmptyArrayToNotExistsFieldsType(layoutStore.fieldsClone),
        [layoutStore.currentKey]: fieldsToJs(layoutStore.currentFieldsSetup),
      };
      await layoutStore.save({
        fields: newStateFields,
      });
      forceReloadNotifications?.({
        showWhatsllyTaskIcon: settings?.showWhatsllyTaskIcon,
        loginProvider: layoutStore.loginProvider,
      });
    }, [forceReloadNotifications, layoutStore, settings?.showWhatsllyTaskIcon]);

    const updateFields = useCallback(
      (fieldsToUpdate: {
        fieldsToDelete: (GetLayoutFieldType | LayoutFieldType | undefined)[];
        fieldsToAdd: (GetLayoutFieldType | LayoutFieldType | undefined)[];
      }) => {
        fieldsToUpdate.fieldsToDelete.map((field) => layoutStore.deleteFieldToFieldsClone(field as LayoutFieldType));
        fieldsToUpdate.fieldsToAdd.map((field) => layoutStore.addFieldToFieldsClone(field as GetLayoutFieldType));
      },
      [layoutStore]
    );

    const handleGetMutateFieldsForFieldsModal = useCallback(() => {
      const fieldsApiNamesToReturn = layoutStore.currentFieldsSetup.filter((field) => !field.d).map((field) => field.a);
      if (layoutStore.currentKey.includes("newEvent")) {
        return uniq([...fieldsApiNamesToReturn, ...DATE_AND_TIME_FIELDS_API_NAMES]);
      }
      return fieldsApiNamesToReturn;
    }, [layoutStore.currentFieldsSetup, layoutStore.currentKey]);

    const openAddFieldModal = useCallback(async () => {
      const nonDeletableFieldNames = handleGetMutateFieldsForFieldsModal();
      const fieldsToUpdate = await modalService.openModal(AddNewLayoutFieldsModal, {
        allFields: layoutStore.selectedLayoutAllFields.filter((field) => !nonDeletableFieldNames.includes(field.name)),
        selectedLayoutFieldsSetup: cloneDeep(layoutStore.currentFieldsSetup),
        maxFields,
      }).promise;

      if (!fieldsToUpdate) return;
      updateFields(fieldsToUpdate);
    }, [handleGetMutateFieldsForFieldsModal, layoutStore.currentFieldsSetup, layoutStore.selectedLayoutAllFields, maxFields, updateFields]);

    const openAddRawFieldModal = useCallback(
      async (field?: LayoutFieldType) => {
        const res = await modalService.openModal(AddEditNewRawFieldModal, { field, isCreate: layoutStore.currentKey?.startsWith("new") }).promise;

        if (!res) return;
        layoutStore.addRawFieldToFieldsClone(res.field);
      },
      [layoutStore]
    );

    return (
      <Page
        className={classnames(styles.page, pageClassName)}
        onCancel={() => handleClosePage(false)}
        cancelButtonClass={styles.cancelButton}
        hideCancelButton={hideCloseAndRefreshButton}
        hideSubmitButton={readOnly}
        submitButtonText={
          !hideCloseAndRefreshButton
            ? layoutStore.needToSave
              ? t("editLayouts.save_changes")
              : t("button_close_and_refresh")
            : t("editLayouts.save_changes")
        }
        loaderClassName={loaderClassName}
        disableSubmit={hideCloseAndRefreshButton && !layoutStore.needToSave}
        isLoading={layoutStore.loading}
        footerClassName={footerClassName}
      >
        <div className={classnames(styles.mainWrapper, mainWrapperClassName)}>
          <FormElement initialValues={[]} onSubmit={() => (layoutStore.needToSave ? onSubmit() : handleClosePage(true))}>
            <div className={styles.main}>
              <div className={styles.selectLayoutTypeContainer} data-testid="selectLayoutTypeContainer">
                <Text size={"small"} weight={`bold`} className={styles.selectLayoutTypeLabel}>
                  {t("editLayouts.select_layout_type")}
                </Text>
                <Select<SelectOptionSingleType | { label: string; options: SelectOptionSingleType[] }>
                  onChange={(value: SelectOptionSingleType | { label: string; options: SelectOptionSingleType[] }) =>
                    layoutStore.changeLayout(value as SelectOptionSingleType, handleCustomObjects)
                  }
                  value={layoutStore.selectedLayout}
                  options={layoutOptions}
                  name={`select_layout_type`}
                  ignoreMenuDirection
                  paddingInline={`none`}
                  data-testid={`select-layout-type-input`}
                />
                {settings?.showRecordTypeLayout && <ManageRecordTypesSection layoutStore={layoutStore} />}
              </div>
              <Separator className={styles.tabsSeparator} />
              <div className={styles.fieldLimitTitle}>{t("editLayouts.select_up_to_display_fields", { numberOfFields: maxFields })}</div>
              <div className={styles.selectedFieldsContainer}>
                <DraggableListComponent
                  onDragEnd={({ newListKeys }) => {
                    const newOrderToSet = sortBy(layoutStore.currentFieldsSetup, (field) => newListKeys.indexOf(`master_${field.a}`));
                    layoutStore.updateItemsPosition(
                      newOrderToSet.map((field, key) => ({
                        ...field,
                        order: key,
                      }))
                    );
                  }}
                >
                  {layoutStore.currentFieldsSetup?.map((field) => (
                    <FieldRow
                      key={`master_${field.a}`}
                      field={field}
                      showRequiredCheckbox={showRequiredCheckbox}
                      onDeleteItem={(field) => layoutStore.deleteFieldToFieldsClone(field)}
                      onUpdateRequired={(field) => layoutStore.updateRequiredToFieldsClone(field)}
                      onEditItem={(field) => openAddRawFieldModal(field)}
                      showEditButton={showEditButton && !readOnly}
                      showDeleteButton={!readOnly}
                    />
                  ))}
                </DraggableListComponent>
              </div>
              <div className={styles.addButtons}>
                {isFieldDropdownEnabled && !hideAddFields && !readOnly && (
                  <div
                    className={classnames(styles.addNewFieldsButton)}
                    onClick={() => {
                      openAddFieldModal();
                    }}
                  >
                    <Svg className={styles.addFieldPlusIcon} iconName={`plus`} width={13.3} height={13.3} />
                    <span>{t("helpers_add_new_field")}</span>
                  </div>
                )}
                {isAddRawFieldEnabled && showAddRawField && !readOnly && (
                  <div
                    className={classnames(styles.addNewFieldsButton)}
                    onClick={() => {
                      openAddRawFieldModal();
                    }}
                  >
                    <Svg className={styles.addFieldPlusIcon} iconName={`plus`} width={13.3} height={13.3} />
                    <span>{`Add Raw Field`}</span>
                  </div>
                )}
              </div>
            </div>
          </FormElement>
        </div>
      </Page>
    );
  }
);
